import React, { useLayoutEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Eye, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ClientServiceGroup, Service } from "../../types/service";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";

interface ClientServicesTableProps {
  groups: ClientServiceGroup[];
  loading: boolean;
  /**
   * The search actually applied to the listing — not the box mid-keystroke.
   *
   * A client found through one of its rates is, on the face of the row, a
   * result with nothing to do with what was typed: the code is one level down.
   * So a search, and only a search, opens the rows it found. Picking a client
   * from the directory does not: that names the row itself, and there is
   * nothing hidden about the answer.
   */
  search?: string;
  onEdit: (service: Service) => void;
  onDelete: (id: string) => void;
}

const th =
  "px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-secondary-600";
const td = "px-3 py-2 align-middle text-[12px] text-gray-800";

/** The chevron plus the five client columns, for the full-width rows. */
const FULL_SPAN = 6;

/** The dash a cell shows when it has nothing to show. */
const blank = <span className="text-gray-400">—</span>;

export const ClientServicesTable: React.FC<ClientServicesTableProps> = ({
  groups,
  loading,
  search,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(["services", "common"]);

  const [expanded, setExpanded] = useState<string[]>([]);
  // Rows the search opened by itself, kept apart from the ones the user opened
  // so that clearing the search puts the listing back the way it was found —
  // without also closing anything the user had opened for their own reasons.
  const [autoExpanded, setAutoExpanded] = useState<string[]>([]);

  const query = search?.trim() ?? "";

  /**
   * The clients on screen, joined into a key.
   *
   * The effect below reacts to the *set* changing rather than to the array
   * identity: the listing refetches after every mutation, and a fresh array
   * each time would re-open rows the user had just closed.
   */
  const groupKey = useMemo(
    () => groups.map((group) => group.partnerId).join(","),
    [groups],
  );

  /*
   * Keyed on the search itself, not merely on whether there is one: each code
   * typed is a new question, and its answers open again even where the reader
   * had closed the previous set by hand. Holding only a boolean here meant a
   * row closed under one search stayed shut for every search after it, as long
   * as the same clients kept matching.
   *
   * Before paint, because `search` arrives with the rows it describes: an
   * ordinary effect would let one frame of collapsed results through first,
   * which reads as the listing opening itself a moment after you have started
   * looking at it.
   */
  useLayoutEffect(() => {
    setAutoExpanded(query && groupKey ? groupKey.split(",") : []);
  }, [query, groupKey]);

  const isExpanded = (partnerId: string) =>
    expanded.includes(partnerId) || autoExpanded.includes(partnerId);

  const toggleExpanded = (partnerId: string) => {
    if (!isExpanded(partnerId)) {
      setExpanded((prev) => [...prev, partnerId]);
      return;
    }
    // Closing clears both lists, or a row the search opened would spring
    // straight back open under the user's own click.
    setExpanded((prev) => prev.filter((id) => id !== partnerId));
    setAutoExpanded((prev) => prev.filter((id) => id !== partnerId));
  };

  if (loading && groups.length === 0) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-gray-50 border-b border-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="sticky top-0 z-10 border-b-2 border-[rgba(11,15,14,0.18)] bg-bone">
          <tr>
            <th className={`${th} w-8`} />
            <th className={th}>{t("services:clientTable.columns.client")}</th>
            <th className={th}>{t("services:clientTable.columns.regCode")}</th>
            <th className={th}>{t("services:clientTable.columns.vat")}</th>
            <th className={th}>
              {t("services:clientTable.columns.billingEmail")}
            </th>
            <th className={th}>
              {t("services:clientTable.columns.paymentTerms")}
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100 bg-white">
          {groups.length === 0 && !loading && (
            <tr>
              <td colSpan={FULL_SPAN} className="px-6 py-12 text-center">
                <h3 className="text-sm font-medium text-gray-900">
                  {query
                    ? t("services:clientTable.noMatches", { query })
                    : t("services:clientTable.noClients")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {query
                    ? t("services:clientTable.noMatchesHint")
                    : t("services:clientTable.noClientsHint")}
                </p>
              </td>
            </tr>
          )}

          {groups.map((group) => {
            const rowExpanded = isExpanded(group.partnerId);
            // With a search on the group holds only what matched, so the row
            // says how many of the client's rates that is — "2" on its own
            // would read as this client's whole rate card.
            const filtered = !!query && group.matchedCount < group.serviceCount;

            return (
              <React.Fragment key={group.partnerId}>
                <tr
                  className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                  onClick={() => toggleExpanded(group.partnerId)}
                >
                  <td className={`${td} w-8`}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(group.partnerId);
                      }}
                      className={`rounded-md border p-0.5 transition-colors ${
                        rowExpanded
                          ? "border-primary-300 bg-primary-50 text-primary-700"
                          : "border-[rgba(11,15,14,0.12)] bg-white text-secondary-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                      }`}
                      aria-expanded={rowExpanded}
                      aria-label={t("services:clientTable.toggle", {
                        client: group.client.name,
                      })}
                      title={t("services:clientTable.toggle", {
                        client: group.client.name,
                      })}
                    >
                      {rowExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  </td>

                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {group.client.name}
                      </span>
                      {/* How many rates are behind the chevron, said on the
                          row itself so the listing can be counted without
                          opening anything. */}
                      <span className="text-gray-500">
                        {filtered
                          ? t("services:clientTable.countMatched", {
                              matched: group.matchedCount,
                              total: group.serviceCount,
                            })
                          : t("services:clientTable.countAll", {
                              count: group.serviceCount,
                            })}
                      </span>
                      {group.client.isDeleted && (
                        <span className="rounded bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">
                          {t("services:form.deleted")}!
                        </span>
                      )}
                    </div>
                  </td>

                  <td className={td}>
                    {group.client.registrationCode || blank}
                  </td>

                  <td className={td}>{group.client.vatNumber || blank}</td>

                  <td className={td}>{group.client.billingEmail || blank}</td>

                  <td className={td}>
                    {group.client.paymentTerms != null
                      ? t("services:clientTable.days", {
                          days: group.client.paymentTerms,
                        })
                      : blank}
                  </td>
                </tr>

                {rowExpanded && (
                  <tr>
                    <td colSpan={FULL_SPAN} className="bg-bone/60 px-3 pb-3">
                      <p className="py-3 text-[12px] text-[#8A9590]">
                        {filtered
                          ? t("services:clientTable.panelMatched", {
                              matched: group.matchedCount,
                              total: group.serviceCount,
                              query,
                            })
                          : t("services:clientTable.panelAll", {
                              count: group.serviceCount,
                            })}
                      </p>

                      {/* `@container` keeps the child table's min-width from
                          escaping into the clients table: a `<td>` sizes to its
                          contents, so the floor below would otherwise be
                          adopted by the outer table and widen the whole page.
                          Sizing this div against its parent instead drops that
                          contribution, and the table scrolls in place. */}
                      <div className="@container overflow-x-auto rounded-md border border-[rgba(11,15,14,0.08)] bg-white">
                        <table className="w-full min-w-[1080px] table-fixed border-collapse">
                          <colgroup>
                            <col className="w-[84px]" />
                            <col className="w-[150px]" />
                            <col />
                            <col className="w-[110px]" />
                            <col className="w-[110px]" />
                            <col className="w-[90px]" />
                            <col className="w-[120px]" />
                            <col className="w-[130px]" />
                            <col className="w-[80px]" />
                          </colgroup>
                          <thead className="border-b border-[rgba(11,15,14,0.08)]">
                            <tr>
                              <th className={th}>{t("services:table.id")}</th>
                              <th className={th}>
                                {t("services:table.serviceCode")}
                              </th>
                              <th className={th}>
                                {t("services:table.description")}
                              </th>
                              <th className={th}>
                                {t("services:table.unitType")}
                              </th>
                              <th className={th}>
                                {t("services:table.price")}
                              </th>
                              <th className={th}>{t("services:table.tax")}</th>
                              <th className={th}>
                                {t("services:table.vatCode")}
                              </th>
                              <th className={th}>
                                {t("services:table.incomeAccount")}
                              </th>
                              <th className={`${th} text-right`}>
                                {t("common:labels.actions")}
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-100">
                            {group.services.map((service) => (
                              <tr
                                key={service._id}
                                className="hover:bg-gray-50"
                              >
                                <td
                                  className={`${td} font-medium text-gray-500`}
                                >
                                  {service.serviceId}
                                </td>
                                <td className={`${td} font-medium`}>
                                  {service.name}
                                </td>
                                <td className={td}>
                                  <p
                                    className="truncate"
                                    title={service.description || "-"}
                                  >
                                    {service.description || "-"}
                                  </p>
                                </td>
                                <td className={td}>{service.type || "-"}</td>
                                <td className={`${td} font-medium`}>
                                  {/* A matrix rate has no single price — the
                                      grid is the price, and €0 would read as
                                      free rather than as "open it". The same
                                      goes for a rate with no default price,
                                      which is agreed per charge: €0 would
                                      state a figure nobody agreed to. */}
                                  {service.type === "MATRIX" ||
                                  service.price == null
                                    ? "—"
                                    : `€${service.price.toFixed(2)}`}
                                </td>
                                <td className={td}>{service.tax}</td>
                                {/* Blank on rates stored before the mapping
                                    existed — the same dash every other empty
                                    cell on this listing shows. */}
                                <td className={td}>
                                  {service.accounting?.vatCode || blank}
                                </td>
                                <td className={td}>
                                  {service.accounting?.incomeAccount || blank}
                                </td>
                                <td className={`${td} text-right`}>
                                  <div className="flex items-center justify-end">
                                    <ActionMenu
                                      ariaLabel={t("common:labels.actions")}
                                      items={
                                        [
                                          {
                                            label: t("common:buttons.view"),
                                            icon: <Eye className="w-4 h-4" />,
                                            onClick: () => onEdit(service),
                                          },
                                          {
                                            label: t("common:buttons.delete"),
                                            icon: (
                                              <Trash2 className="w-4 h-4" />
                                            ),
                                            onClick: () =>
                                              onDelete(service._id),
                                            variant: "danger",
                                            divider: true,
                                          },
                                        ] as ActionMenuItem[]
                                      }
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
